import React, { useEffect, useMemo, useState } from 'react';
import { IconChevronRight, IconHelp, IconX, IconUser, IconClock, IconMapPin } from '@tabler/icons-react';
interface GanttPerson {
  name: string;
  avatar: string;
}
interface GanttActivity {
  id: string;
  title: string;
  child: GanttPerson;
  assignee: GanttPerson | null;
  categoryColor: string;
  startDay: number;
  endDay: number;
  time: string | null;
}
interface MiniGanttProps {
  onViewAll?: () => void;
}
const ITALIAN_DAYS = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const NUM_DAYS = 7;
function generateDays(count: number) {
  const days: {
    label: string;
    date: number;
    isToday: boolean;
  }[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    days.push({
      label: ITALIAN_DAYS[d.getDay()],
      date: d.getDate(),
      isToday: i === 0
    });
  }
  return days;
}
const mockGanttActivities: GanttActivity[] = [{
  id: 'g2',
  title: 'Corso nuoto',
  child: {
    name: 'Marco',
    avatar: 'https://cdn.magicpatterns.com/uploads/1EXQ4FkeqG6UuqJKWikck9/tatev-arsh-ZtB5Haz58os-unsplash.jpg'
  },
  assignee: {
    name: 'Luca',
    avatar: 'https://cdn.magicpatterns.com/uploads/7G4z2y5zxpC8H8Kk9BNvv6/felipe-salgado-zssU4tUQ-18-unsplash.jpg'
  },
  categoryColor: '#DDD6FE',
  startDay: 1,
  endDay: 1,
  time: '16:00'
}, {
  id: 'g3',
  title: 'Iscrizione scuola',
  child: {
    name: 'Sofia',
    avatar: 'https://cdn.magicpatterns.com/uploads/c3jxasi4fCCZ93c9aemubP/adele-shafiee-KCvjle4_4ps-unsplash.jpg'
  },
  assignee: {
    name: 'Maria',
    avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face'
  },
  categoryColor: '#AEDAFF',
  startDay: 2,
  endDay: 4,
  time: null
}, {
  id: 'g4',
  title: 'Comprare quaderni',
  child: {
    name: 'Marco',
    avatar: 'https://cdn.magicpatterns.com/uploads/1EXQ4FkeqG6UuqJKWikck9/tatev-arsh-ZtB5Haz58os-unsplash.jpg'
  },
  assignee: null,
  categoryColor: '#FAD88A',
  startDay: 3,
  endDay: 3,
  time: null
}, {
  id: 'g5',
  title: 'Visita oculistica',
  child: {
    name: 'Marco',
    avatar: 'https://cdn.magicpatterns.com/uploads/1EXQ4FkeqG6UuqJKWikck9/tatev-arsh-ZtB5Haz58os-unsplash.jpg'
  },
  assignee: {
    name: 'Luca',
    avatar: 'https://cdn.magicpatterns.com/uploads/7G4z2y5zxpC8H8Kk9BNvv6/felipe-salgado-zssU4tUQ-18-unsplash.jpg'
  },
  categoryColor: '#B0EC9C',
  startDay: 5,
  endDay: 5,
  time: '09:00'
}];
export function MiniGantt({
  onViewAll
}: MiniGanttProps) {
  const days = useMemo(() => generateDays(NUM_DAYS), []);
  const [selectedActivity, setSelectedActivity] = useState<GanttActivity | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const openModal = (activity: GanttActivity) => {
    setSelectedActivity(activity);
    // Trigger animation on next frame
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsModalVisible(true);
      });
    });
  };
  const closeModal = () => {
    setIsModalVisible(false);
    setTimeout(() => {
      setSelectedActivity(null);
    }, 300); // match transition duration
  };
  // Generate day label for activity
  const getDayLabel = (startDay: number, endDay: number) => {
    const dayData = generateDays(NUM_DAYS);
    if (startDay === endDay) {
      const d = dayData[startDay];
      return d ? d.isToday ? 'Oggi' : `${d.label} ${d.date}` : '';
    }
    const start = dayData[startDay];
    const end = dayData[endDay];
    if (start && end) {
      return `${start.isToday ? 'Oggi' : `${start.label} ${start.date}`} → ${end.isToday ? 'Oggi' : `${end.label} ${end.date}`}`;
    }
    return '';
  };
  return <section data-id="element-195">
      {/* Section header */}
      <div className="flex items-center justify-between mb-3" data-id="element-196">
        <h2 className="font-rubik font-semibold text-xs uppercase tracking-wider text-yeppy-text text-opacity-40" data-id="element-197">
          Prossimi Giorni
        </h2>
        {onViewAll && <button onClick={onViewAll} className="font-poppins text-sm text-yeppy-text text-opacity-60 flex items-center gap-1" data-id="element-198">
            Vedi tutto
            <IconChevronRight className="w-4 h-4" stroke={1.5} data-id="element-199" />
          </button>}
      </div>

      {/* Card container */}
      <div className="bg-white border border-yeppy-text border-opacity-10 rounded-2xl overflow-hidden" data-id="element-200">
        {/* Day header row — full width, 7 equal columns */}
        <div className="grid border-b border-yeppy-text/5" style={{
        gridTemplateColumns: `repeat(${NUM_DAYS}, 1fr)`
      }} data-id="element-201">
          {days.map((day, i) => <div key={i} className="flex flex-col items-center justify-center py-2.5" data-id="element-202">
              <span className={`font-poppins text-xs uppercase tracking-wider mb-0.5 ${day.isToday ? 'text-yeppy-text font-semibold' : 'text-yeppy-text/40 font-medium'}`} data-id="element-203">
                {day.isToday ? 'Oggi' : day.label}
              </span>
              {day.isToday ? <span className="w-7 h-7 rounded-full bg-yeppy-text text-white flex items-center justify-center font-rubik text-xs font-semibold" data-id="element-204">
                  {day.date}
                </span> : <span className="w-7 h-7 rounded-full flex items-center justify-center font-rubik text-xs text-yeppy-text/60 font-medium" data-id="element-205">
                  {day.date}
                </span>}
            </div>)}
        </div>

        {/* Activity rows */}
        {mockGanttActivities.map((activity, actIdx) => <div key={activity.id} onClick={() => openModal(activity)} className={`cursor-pointer transition-colors hover:bg-yeppy-bg/50 active:bg-yeppy-bg/80 ${actIdx < mockGanttActivities.length - 1 ? 'border-b border-yeppy-text/5' : ''}`} data-id="element-206">
            {/* Info row — full width, child + title + caregiver */}
            <div className="flex items-center gap-2.5 px-3 py-3" data-id="element-207">
              {/* Child avatar */}
              <img src={activity.child.avatar} alt={activity.child.name} className="w-8 h-8 rounded-full object-cover border border-yeppy-text/10 flex-shrink-0" data-id="element-208" />
              {/* Title + child name */}
              <div className="flex-1 min-w-0 flex items-baseline gap-1.5" data-id="element-209">
                <span className="font-poppins text-sm font-medium text-yeppy-text truncate" data-id="element-210">
                  {activity.title}
                </span>
                <span className="font-poppins text-[11px] text-yeppy-text/40 flex-shrink-0" data-id="element-211">
                  {activity.child.name}
                </span>
              </div>
              {/* Time pill */}
              {activity.time && <span className="font-poppins text-[11px] font-medium text-yeppy-text/60 px-1.5 py-0.5 rounded-md flex-shrink-0" style={{
            backgroundColor: `${activity.categoryColor}25`
          }} data-id="element-212">
                  {activity.time}
                </span>}
              {/* Caregiver */}
              {activity.assignee ? <img src={activity.assignee.avatar} alt={activity.assignee.name} className="w-6 h-6 rounded-full object-cover border border-yeppy-text/10 flex-shrink-0" data-id="element-213" /> : <div className="w-6 h-6 rounded-full bg-yeppy-bg border border-yeppy-text/10 flex items-center justify-center flex-shrink-0" data-id="element-214">
                  <IconHelp className="w-3 h-3 text-yeppy-text/40" stroke={1.5} data-id="element-215" />
                </div>}
            </div>
          </div>)}
      </div>

      {/* Detail Modal */}
      {selectedActivity && <div className="fixed inset-0 z-50 flex items-center justify-center px-6" onClick={closeModal} data-id="element-216">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/30 transition-opacity duration-300" style={{
        opacity: isModalVisible ? 1 : 0
      }} data-id="element-217" />

          {/* Modal Card */}
          <div onClick={e => e.stopPropagation()} className="relative w-full max-w-sm bg-white rounded-2xl border border-yeppy-text/10 overflow-hidden transition-all duration-300" style={{
        transform: isModalVisible ? 'translateY(0)' : 'translateY(60px)',
        opacity: isModalVisible ? 1 : 0,
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)'
      }} data-id="element-218">
            {/* Color accent top bar */}
            <div className="h-1.5 w-full" style={{
          backgroundColor: selectedActivity.categoryColor
        }} data-id="element-219" />

            {/* Close button */}
            <button onClick={closeModal} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-yeppy-bg flex items-center justify-center transition-colors" data-id="element-220">
              <IconX className="w-4 h-4 text-yeppy-text/60" stroke={1.5} data-id="element-221" />
            </button>

            <div className="p-5 pt-5" data-id="element-222">
              {/* Child info */}
              <div className="flex items-center gap-3 mb-4" data-id="element-223">
                <img src={selectedActivity.child.avatar} alt={selectedActivity.child.name} className="w-12 h-12 rounded-full object-cover border-2 border-yeppy-text/10" data-id="element-224" />
                <div data-id="element-225">
                  <h3 className="font-rubik font-semibold text-lg text-yeppy-text" data-id="element-226">
                    {selectedActivity.title}
                  </h3>
                  <p className="font-poppins text-sm text-yeppy-text/50" data-id="element-227">
                    {selectedActivity.child.name}
                  </p>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-3" data-id="element-228">
                {/* When */}
                <div className="flex items-center gap-3 bg-yeppy-bg/60 rounded-xl px-4 py-3" data-id="element-229">
                  <IconClock className="w-5 h-5 text-yeppy-text/50" stroke={1.5} data-id="element-230" />
                  <div data-id="element-231">
                    <p className="font-poppins text-xs text-yeppy-text/40 mb-0.5" data-id="element-232">
                      Quando
                    </p>
                    <p className="font-poppins text-sm font-medium text-yeppy-text" data-id="element-233">
                      {getDayLabel(selectedActivity.startDay, selectedActivity.endDay)}
                      {selectedActivity.time ? ` · ${selectedActivity.time}` : ''}
                    </p>
                  </div>
                </div>

                {/* Assignee */}
                <div className="flex items-center gap-3 bg-yeppy-bg/60 rounded-xl px-4 py-3" data-id="element-234">
                  <IconUser className="w-5 h-5 text-yeppy-text/50" stroke={1.5} data-id="element-235" />
                  <div className="flex-1" data-id="element-236">
                    <p className="font-poppins text-xs text-yeppy-text/40 mb-0.5" data-id="element-237">
                      Assegnato a
                    </p>
                    {selectedActivity.assignee ? <div className="flex items-center gap-2" data-id="element-238">
                        <img src={selectedActivity.assignee.avatar} alt={selectedActivity.assignee.name} className="w-6 h-6 rounded-full object-cover border border-yeppy-text/10" data-id="element-239" />
                        <span className="font-poppins text-sm font-medium text-yeppy-text" data-id="element-240">
                          {selectedActivity.assignee.name}
                        </span>
                      </div> : <span className="font-poppins text-sm text-yeppy-text/50" data-id="element-241">
                        Non assegnato
                      </span>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </section>;
}